/**
 * Post-Stripe success page for VOX Store Phase 1.
 *
 * URL: /subscriptions/success?session_id=cs_test_...
 *
 * Behaviour:
 *   1. Extract session_id from the query string.
 *   2. Poll `GET /api/subscriptions/checkout/{session_id}` until
 *      payment_status === 'paid' (or timeout ~30s).
 *   3. On success, offer a "Go to Dashboard" link.
 *   4. On unresolved poll, offer "Try again / Contact support".
 *
 * The status endpoint asks Stripe directly if the webhook is delayed,
 * so this page confirms the real Stripe payment state — not a local
 * DB write.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API } from '@/App';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SATISFIND_LOGO = 'https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/s8cmwdge_satisFIND%2020th%20Logo.png';

const PRODUCT_LABEL = {
  nps: 'NPS®', csat: 'CSAT', voc: 'Voice of Customer', voe: 'Voice of Employee',
};

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15; // ~30 seconds

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [state, setState] = useState({ phase: 'polling', attempts: 0, data: null, error: '' });
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    if (!sessionId) {
      setState({ phase: 'error', error: 'Missing session identifier.', attempts: 0, data: null });
      return;
    }
    let attempts = 0;
    const tick = async () => {
      if (stopRef.current) return;
      attempts += 1;
      try {
        const res = await fetch(`${API}/subscriptions/checkout/${encodeURIComponent(sessionId)}`);
        const data = res.ok ? await res.json() : null;
        if (data && data.payment_status === 'paid') {
          setState({ phase: 'done', attempts, data, error: '' });
          return;
        }
        if (attempts >= POLL_MAX_ATTEMPTS) {
          setState({ phase: 'timeout', attempts, data, error: '' });
          return;
        }
        setState({ phase: 'polling', attempts, data, error: '' });
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempts >= POLL_MAX_ATTEMPTS) {
          setState({ phase: 'error', attempts, data: null, error: 'Network error while confirming your payment.' });
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();
    return () => { stopRef.current = true; };
  }, [sessionId]);

  const productLabel = PRODUCT_LABEL[state.data?.product] || state.data?.product || '';

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)' }}
      data-testid="subscription-success-page"
    >
      <img src={SATISFIND_LOGO} alt="satisFIND" style={{ width: 160, height: 'auto' }} className="mb-8" />
      <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-2xl shadow-md p-8 space-y-5">
        {state.phase === 'polling' && (
          <div className="text-center" data-testid="success-state-polling">
            <Loader2 className="w-12 h-12 mx-auto text-[#1A3E6F] animate-spin" />
            <h1 className="mt-4 text-xl font-bold font-['Montserrat'] text-[#0F172A]">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-[#64748B]">
              Stripe is finalising your subscription. This usually takes a few seconds.
            </p>
          </div>
        )}
        {state.phase === 'done' && (
          <div className="text-center" data-testid="success-state-done">
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#22C55E]" />
            <h1 className="mt-4 text-2xl font-bold font-['Montserrat'] text-[#0F172A]">Payment confirmed</h1>
            <p className="mt-2 text-sm text-[#0F172A]">
              You're now subscribed to <strong>{productLabel}</strong>
              {state.data?.billing_cycle ? <> — <span className="lowercase">{state.data.billing_cycle}</span></> : null}
              {state.data?.location_count ? <> · {state.data.location_count} location{state.data.location_count > 1 ? 's' : ''}</> : null}
              {state.data?.region ? <> · pricing region: {state.data.region === 'sea' ? 'India + SEA' : 'Rest of World'}</> : null}
              .
            </p>
            <p className="mt-4 text-xs text-[#64748B]">
              Your VOX access is being provisioned. If your dashboard shows a pending state, refresh in a minute.
            </p>
            {/* Sept 2026 — first-time buyers land on the Subscription
                page so they can immediately confirm product, cycle,
                paid Locations and billing history. Returning buyers
                (add-on purchases) still land on Dashboard. */}
            <Link
              to="/admin/subscription"
              className="inline-flex items-center justify-center mt-6 px-6 py-3 rounded-full bg-[#1A3E6F] hover:bg-[#122c50] text-white font-semibold transition-colors"
              data-testid="success-goto-subscription"
            >
              View your subscription
            </Link>
          </div>
        )}
        {state.phase === 'timeout' && (
          <div className="text-center" data-testid="success-state-timeout">
            <AlertCircle className="w-14 h-14 mx-auto text-[#F59E0B]" />
            <h1 className="mt-4 text-xl font-bold font-['Montserrat'] text-[#0F172A]">Still confirming…</h1>
            <p className="mt-2 text-sm text-[#64748B]">
              Stripe hasn't confirmed the payment yet. Your card may already have been charged. You can safely refresh, or
              open your dashboard — it will unlock automatically once Stripe finishes.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
              <Button onClick={() => window.location.reload()} className="bg-[#1A3E6F] hover:bg-[#122c50] text-white">
                Refresh
              </Button>
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center px-5 py-2 rounded-lg border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
              >
                Go to dashboard
              </Link>
            </div>
          </div>
        )}
        {state.phase === 'error' && (
          <div className="text-center" data-testid="success-state-error">
            <AlertCircle className="w-14 h-14 mx-auto text-[#DC2626]" />
            <h1 className="mt-4 text-xl font-bold font-['Montserrat'] text-[#0F172A]">We couldn't confirm your payment</h1>
            <p className="mt-2 text-sm text-[#64748B]">{state.error || 'Please contact support@satisfind.com.'}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center px-5 py-2 rounded-lg border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
              >
                Back to pricing
              </Link>
            </div>
          </div>
        )}
      </div>
      <p className="text-xs text-[#94A3B8] mt-8">
        Powered by <span className="font-semibold">satisFIND®</span>
      </p>
    </div>
  );
}
